import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { getMySpacesFeedApiV1QuestionsMySpacesFeedGet } from '@api-client/api-client';
import { useEventStreamState } from '@hooks/useEventStreamState';
import { useQuestionEntities } from '@hooks/useQuestionEntities';
import { refreshInfiniteQuery } from '@utils/refreshInfiniteQuery';
import type { SortOption, UseHomeFeedReturn } from '../types';
import {
  questionMySpacesFeedKey,
  resolveNextPageParam,
  upsertQuestionEntities,
  type PageParam,
  type NormalizedPage,
} from '@lib/cache/questions';

const QUESTIONS_PER_PAGE = 20;

interface UseMySpacesFeedOptions {
  sortBy: SortOption;
  enabled?: boolean;
}

/**
 * Infinite scroll hook for the My Spaces aggregated feed.
 * Returns questions from all spaces the user has joined.
 * Server-side sorted via sort_by parameter.
 */
export function useMySpacesFeed({
  sortBy,
  enabled = true,
}: UseMySpacesFeedOptions): UseHomeFeedReturn {
  const queryClient = useQueryClient();
  const { isConnected } = useEventStreamState();
  const serverSortBy = sortBy === 'bookmarked' ? 'latest' : sortBy;

  const queryKey = useMemo(() => questionMySpacesFeedKey(serverSortBy), [serverSortBy]);

  const {
    data: idPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
  } = useInfiniteQuery<NormalizedPage>({
    queryKey,
    queryFn: async ({ pageParam, signal }) => {
      const params: {
        cursor?: number;
        page?: number;
        per_page: number;
        sort_by: string;
      } = {
        per_page: QUESTIONS_PER_PAGE,
        sort_by: serverSortBy,
      };

      const typedPageParam = pageParam as PageParam | null;
      if (typedPageParam && 'cursor' in typedPageParam) {
        params.cursor = typedPageParam.cursor;
      } else if (typedPageParam && 'page' in typedPageParam) {
        params.page = typedPageParam.page;
      }

      const response = await getMySpacesFeedApiV1QuestionsMySpacesFeedGet(params, { signal });
      const questions = response.data.data;

      signal.throwIfAborted();

      upsertQuestionEntities(queryClient, questions);

      return {
        ids: questions.map(q => q.id),
        pagination: response.data.pagination,
        meta: response.data.meta,
      };
    },
    enabled,
    initialPageParam: null as PageParam | null,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    // AIDEV-NOTE: When SSE is connected, targeted invalidations keep this feed
    // fresh in real time, so the 60s poll is redundant. The signals that actually
    // refresh questionMySpacesFeedKey: space.question_created (new posts in a
    // joined space), space.topic_changed / space.updated (denormalized topic name
    // and space name/image on the cards), space.member_* for self (join/leave), and
    // the broad ['questions']-prefix sweeps from question.updated / question.deleted
    // / user.updated (P5, author identity). question.created IS emitted as of P4b
    // for spaceless questions, but its handler deliberately does not sweep this
    // feed: spaceless posts never appear in the my-spaces feed, and space-linked
    // posts arrive here via space.question_created instead (see
    // docs/sse-realtime/ROADMAP.md). Fall back to polling only while disconnected.
    // The query stays enabled either way — a disabled query ignores invalidation,
    // which would defeat the SSE path.
    //
    // Residual staleness (accepted): denormalized fields with no SSE event still go
    // stale on a connected client. Author identity (full_name / profile_picture) is
    // now covered by user.updated (P5); what remains is any field without an event,
    // which self-heals on focus refetch, an incidental ['questions'] sweep, or
    // FULL_SYNC on reconnect.
    refetchInterval: isConnected ? false : 60 * 1000,
    refetchIntervalInBackground: false,
    getNextPageParam: (lastPage, allPages) =>
      resolveNextPageParam(lastPage, allPages, { hookName: 'useMySpacesFeed' }),
  });

  const ids = useMemo(() => idPages?.pages.flatMap(p => p.ids) ?? [], [idPages]);

  const questions = useQuestionEntities(ids, { gcTime: 10 * 60 * 1000 });

  const refresh = useCallback(
    () => refreshInfiniteQuery(queryClient, queryKey),
    [queryClient, queryKey],
  );

  const fetchNextPageStable = useCallback(() => {
    void fetchNextPage();
  }, [fetchNextPage]);

  return {
    questions,
    isLoading,
    isLoadingMore: isFetchingNextPage,
    hasNextPage,
    fetchNextPage: fetchNextPageStable,
    isEmpty: !isLoading && questions.length === 0,
    feedType: 'my-spaces',
    isError,
    error: error ?? null,
    refresh,
  };
}
